/**
 * Channel definitions per SPEC §4.4.
 *
 * Each channel describes:
 *   - the lightness/chroma ranges used to draw the two blob base colors,
 *   - the mean hue offset between the two blobs (and how much jitter to add),
 *   - per-hotspot derivation jitter (hue / L / C),
 *   - and (for Mono) an explicit per-role lightness split so the two blobs
 *     are always visually distinct.
 *
 * The `derive(base, rng)` helper produces 3–4 hotspot OKLCH samples around a
 * base color using the channel's hotspot jitter spec. All randomness is
 * routed through the `rng` parameter — never `Math.random()`.
 */

import type { ChannelId, OKLCH } from '../types.js';

export interface HotspotJitter {
  /** Hue jitter in degrees (symmetric, e.g. 20 → ±20°). */
  hue: number;
  /** Lightness jitter (symmetric, e.g. 0.10 → ±0.10). */
  l: number;
  /** Chroma jitter (symmetric, e.g. 0.04 → ±0.04). */
  c: number;
}

export interface ChannelDefinition {
  id: ChannelId;
  lRange: [number, number];
  cRange: [number, number];
  /** Mean hue offset between blob A and B base hues (in degrees). */
  hueOffsetDeg: number;
  /** Symmetric jitter on the hue offset (0 = exact). */
  hueOffsetJitterDeg: number;
  hotspotJitter: HotspotJitter;
  /**
   * For Mono only: per-role lightness ranges. Other channels leave this
   * undefined and use `lRange` for both blobs.
   */
  monoLightnessSplit?: {
    aRange: [number, number];
    bRange: [number, number];
  };
  /**
   * Optional bias on the base hue — used by Ch3 to nudge toward warm hues.
   * Returns true if the hue is acceptable; the sampler will retry until it
   * passes (with a bounded number of attempts).
   */
  hueFilter?: (hue: number) => boolean;
  /** Generate 3–4 hotspot OKLCHs around `base` using this channel's jitter. */
  derive: (base: OKLCH, rng: () => number) => OKLCH[];
}

// ─── helpers ────────────────────────────────────────────────────────────────

const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const normHue = (h: number): number => {
  const m = h % 360;
  return m < 0 ? m + 360 : m;
};
const symJitter = (rng: () => number, range: number): number =>
  (rng() * 2 - 1) * range;

/**
 * Build a derive() function for a given hotspot-jitter spec.
 * Produces 3 or 4 hotspots (count chosen via rng).
 */
function makeDerive(
  jitter: HotspotJitter,
  cRangeClamp?: [number, number],
): (base: OKLCH, rng: () => number) => OKLCH[] {
  return (base: OKLCH, rng: () => number): OKLCH[] => {
    const count = rng() < 0.5 ? 3 : 4;
    const out: OKLCH[] = [];
    for (let i = 0; i < count; i++) {
      const h = normHue(base.h + symJitter(rng, jitter.hue));
      const l = clamp01(base.l + symJitter(rng, jitter.l));
      let c = base.c + symJitter(rng, jitter.c);
      if (cRangeClamp) {
        c = Math.min(cRangeClamp[1], Math.max(cRangeClamp[0], c));
      } else {
        c = Math.max(0, c);
      }
      out.push({ l, c, h });
    }
    return out;
  };
}

// Warm-bias predicate for Ch3. Accepts hues roughly in the warm arc
// (red-orange-yellow); rejection sampling around this arc drifts the
// dusty palette toward warmth without being a hard constraint.
const warmFilter = (hue: number): boolean => {
  const h = normHue(hue);
  return h >= 20 && h <= 100;
};

// ─── channel table ──────────────────────────────────────────────────────────

export const CHANNELS: Record<ChannelId, ChannelDefinition> = {
  // Ch1 — Refined Complementary
  ch1: {
    id: 'ch1',
    lRange: [0.55, 0.75],
    cRange: [0.10, 0.18],
    hueOffsetDeg: 180,
    hueOffsetJitterDeg: 15,
    hotspotJitter: { hue: 20, l: 0.10, c: 0.04 },
    derive: makeDerive({ hue: 20, l: 0.10, c: 0.04 }),
  },

  // Ch2 — Bold Complementary
  ch2: {
    id: 'ch2',
    lRange: [0.55, 0.75],
    cRange: [0.20, 0.32],
    hueOffsetDeg: 180,
    hueOffsetJitterDeg: 10,
    // SPEC §4.4 specifies hue ±15° for Ch2. L/C jitters mirror Ch1's spirit
    // (the spec only lists the hue figure explicitly).
    hotspotJitter: { hue: 15, l: 0.10, c: 0.04 },
    derive: makeDerive({ hue: 15, l: 0.10, c: 0.04 }),
  },

  // Ch3 — Muted / Dusty (warm-biased)
  ch3: {
    id: 'ch3',
    lRange: [0.50, 0.85],
    cRange: [0.04, 0.10],
    hueOffsetDeg: 180,
    hueOffsetJitterDeg: 30,
    hotspotJitter: { hue: 25, l: 0.15, c: 0.04 },
    hueFilter: warmFilter,
    derive: makeDerive({ hue: 25, l: 0.15, c: 0.04 }),
  },

  // Mono — Monochromatic Midtones
  mono: {
    id: 'mono',
    // Single canonical L range; mono uses monoLightnessSplit for per-role L.
    lRange: [0.40, 0.80],
    cRange: [0.00, 0.02],
    hueOffsetDeg: 0,
    hueOffsetJitterDeg: 0,
    hotspotJitter: { hue: 0, l: 0.10, c: 0.0 },
    monoLightnessSplit: {
      aRange: [0.65, 0.80],
      bRange: [0.40, 0.55],
    },
    // Hotspots only jitter L; chroma stays tightly inside [0, 0.02] so the
    // palette remains monochromatic.
    derive: makeDerive({ hue: 0, l: 0.10, c: 0.0 }, [0, 0.02]),
  },
};
